import { useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import * as AgenticV2API from "@/services/api/agentic-v2";
import type { AgentSummary } from "@/services/api/agentic-v2";
import { useWindowLive } from "@/components/ui/os-layout/WindowActiveContext";
import type {
  MissionControlEventSubscription,
  V2InteractionCompletedPayload,
  V2InteractionStartedPayload,
  V2MissionCompletedPayload,
  V2MissionStageChangedPayload,
  V2TaskCreatedPayload,
  V2TaskNeedsAttentionPayload,
  V2TaskStatusChangedPayload,
} from "@/context/MissionControlWebSocketContext";
import { missionControlKeys } from "./keys";
import {
  applyMissionStageChangedToMissionDetailCache,
  applyTaskCreatedToMissionDetailCache,
  applyTaskStatusChangedToMissionDetailCache,
  invalidateMissionDetail,
  patchMissionDetailFields,
  patchMissionDetailFromSummary,
  type MissionDetailBundle,
} from "./patches";
import { invalidateMissionStepLog } from "./useMissionStepLog";

export interface UseMissionDetailOptions {
  missionId: string;
  enabled?: boolean;
}

export interface MissionDetailQueryData extends MissionDetailBundle {
  agents: AgentSummary[];
}

/**
 * Cached mission detail for the standalone mission window.
 * Pauses fetching when the OS window is minimised, unfocused, or in a background workspace.
 */
export function useMissionDetail({
  missionId,
  enabled: enabledOption = true,
}: UseMissionDetailOptions) {
  const isWindowLive = useWindowLive();
  const queryEnabled = enabledOption && isWindowLive && Boolean(missionId);

  return useQuery({
    queryKey: missionControlKeys.missionDetail(missionId),
    enabled: queryEnabled,
    refetchOnWindowFocus: queryEnabled,
    queryFn: async (): Promise<MissionDetailQueryData> => {
      const [mission, agents] = await Promise.all([
        AgenticV2API.getMission(missionId),
        AgenticV2API.listAgents(),
      ]);
      return { mission, agents };
    },
  });
}

/** QueryClient accessor for mission-detail cache patches (WS events). */
export function useMissionDetailQueryClient() {
  return useQueryClient();
}

function matchesMission(missionId: string, payloadMissionId: string | null | undefined): boolean {
  return payloadMissionId === missionId;
}

/** WebSocket handlers that patch or invalidate the cached mission detail document. */
export function createMissionDetailWsSubscription(
  queryClient: QueryClient,
  missionId: string,
): MissionControlEventSubscription {
  return {
    onMissionStageChanged: (payload: V2MissionStageChangedPayload) => {
      if (!matchesMission(missionId, payload.mission_id)) return;
      applyMissionStageChangedToMissionDetailCache(queryClient, payload);
    },
    onMissionCompleted: (payload: V2MissionCompletedPayload) => {
      if (!matchesMission(missionId, payload.mission_id)) return;
      if (payload.mission) {
        patchMissionDetailFromSummary(queryClient, missionId, payload.mission);
        return;
      }
      patchMissionDetailFields(queryClient, missionId, {
        status: payload.status,
      });
    },
    onTaskCreated: (payload: V2TaskCreatedPayload) => {
      if (!matchesMission(missionId, payload.mission_id)) return;
      applyTaskCreatedToMissionDetailCache(queryClient, payload);
    },
    onTaskStatusChanged: (payload: V2TaskStatusChangedPayload) => {
      if (!matchesMission(missionId, payload.mission_id)) return;
      applyTaskStatusChangedToMissionDetailCache(queryClient, payload);
      invalidateMissionStepLog(queryClient, missionId);
    },
    onTaskNeedsAttention: (payload: V2TaskNeedsAttentionPayload) => {
      if (!matchesMission(missionId, payload.mission_id)) return;
      invalidateMissionDetail(queryClient, missionId);
    },
    onInteractionStarted: (payload: V2InteractionStartedPayload) => {
      if (!matchesMission(missionId, payload.mission_id)) return;
      invalidateMissionDetail(queryClient, missionId);
    },
    onInteractionCompleted: (payload: V2InteractionCompletedPayload) => {
      if (!matchesMission(missionId, payload.mission_id)) return;
      invalidateMissionDetail(queryClient, missionId);
    },
  };
}
